/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { IMenu, IMenuItem } from "./IMenu";

interface IMenuRegistry {
	[K: string]: {
		menu: IMenu;
		items: IMenuItem[];
		itemIndex: { [K: string]: IMenuItem };
	};
}

/**
 * Service options
 */
export interface IMenuServiceOpts {}

/**
 * Service to manage menus and items
 */
export class MenuService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** Registered menus */
	private menuRegistry: IMenuRegistry = {};

	/**
	 * Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(private dc: IEditor, opts: IMenuServiceOpts) {
		this.logger = dc.get("logger").facility("MenuService");
	}

	/**
	 * Registers menu
	 *
	 * @param menu Menu definition
	 */
	public registerMenu(menu: IMenu): void {
		if (this.menuRegistry[menu.id]) {
			throw new Error(`Menu with ID '${menu.id}' is already registered.`);
		}

		const menuEntry = {
			menu: menu,
			items: [],
			itemIndex: {}
		};

		if (menu.items) {
			for (let i = 0; i < menu.items.length; i++) {
				if (menuEntry.itemIndex[menu.items[i].id]) {
					throw new Error(
						`Failed to register menu ID '${menu.id}': Duplicate item ID '${menu.items[i].id}'.`
					);
				}

				menuEntry.items.push(menu.items[i]);
				menuEntry.itemIndex[menu.items[i].id] = menu.items[i];
			}

			menuEntry.items.sort(sortMenuItems);
		}

		this.menuRegistry[menu.id] = menuEntry;
	}

	/**
	 * Registers menu item
	 *
	 * @param menuId Menu ID
	 * @param menuItem Menu item definition
	 */
	public registerMenuItem(menuId: string, menuItem: IMenuItem): void {
		const menuEntry = this.menuRegistry[menuId];

		if (!menuEntry) {
			throw new Error(`Menu with ID '${menuId}' is not registered.`);
		}

		if (menuEntry.itemIndex[menuItem.id]) {
			throw new Error(`Menu with ID '${menuId}' already has item ID '${menuItem.id}' registered.`);
		}

		menuEntry.items.push(menuItem);
		menuEntry.itemIndex[menuItem.id] = menuItem;

		menuEntry.items.sort(sortMenuItems);
	}

	/**
	 * Returns if a menu is registered
	 *
	 * @param menuId Menu ID
	 */
	public isMenuRegistered(menuId: string): boolean {
		return this.menuRegistry[menuId] ? true : false;
	}

	/**
	 * Returns if a menu item is registered
	 *
	 * @param menuId Menu ID
	 * @param itemId Item ID
	 */
	public isMenuItemRegistered(menuId: string, itemId: string): boolean {
		return this.menuRegistry[menuId] && this.menuRegistry[menuId].itemIndex[itemId] ? true : false;
	}

	/**
	 * Returns menu items. Throws if menu is not registered.
	 *
	 * @param menuId Menu ID
	 */
	public getMenuItems(menuId: string): IMenuItem[] {
		const menuEntry = this.menuRegistry[menuId];

		if (!menuEntry) {
			throw new Error(`Menu with ID '${menuId}' is not registered.`);
		}

		return menuEntry.items;
	}

	/**
	 * Returns all menus object
	 */
	public getAllMenus(): IMenuRegistry {
		return this.menuRegistry;
	}

	/**
	 * Executes command associated with menu item
	 *
	 * @param menuId Menu ID
	 * @param itemId Item ID
	 */
	public async dispatchMenuItem(menuId: string, itemId: string): Promise<unknown> {
		const menuEntry = this.menuRegistry[menuId];

		if (!menuEntry) {
			throw new Error(`Menu with ID '${menuId}' is not registered.`);
		}

		const menuItem = menuEntry.itemIndex[itemId];

		if (!menuItem) {
			throw new Error(`Menu item '${menuId}:${itemId}' is not registered.`);
		}

		if (menuItem.isEnabled && !menuItem.isEnabled()) {
			throw new Error(`Menu item '${menuId}:${itemId}' is disabled.`);
		}

		this.logger.debug(`Dispatching menu item '${menuId}:${itemId}'.`);

		return this.dc.get("commandsService").execute(menuItem.commandId, ...(menuItem.commandArgs || []));
	}
}

function sortMenuItems(a: IMenuItem, b: IMenuItem) {
	return a.order > b.order ? 1 : a.order < b.order ? -1 : 0;
}
